import { type ReactElement } from "react";
import { useSession } from "@baseline/web/auth";
import { supabase } from "@baseline/web/supabase";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

export const Route = createFileRoute("/context")({
	component: ContextPage,
	validateSearch: (search: Record<string, unknown>) => ({
		view: search.view === "json" ? ("json" as const) : undefined,
	}),
});

interface StarredDoc {
	documentId: string;
	title: string;
	doc_type: string | null;
	summary: string | null;
	inbound_edge_count: number;
	updated_at: string | null;
	edges: { relation: string; neighborId: string; neighbor_title: string }[];
}

interface SessionItem {
	sessionId: string;
	name: string;
	summary: string | null;
	project: string | null;
	created_at: string;
	updated_at: string;
	documents: {
		documentId: string;
		title: string;
		doc_type: string | null;
		summary: string | null;
	}[];
}

interface ThinkingDoc {
	documentId: string;
	title: string;
	doc_type: string | null;
	summary: string | null;
	inbound_edge_count: number;
	updated_at: string | null;
	session: string | null;
}

interface LibraryItem {
	title: string;
	url: string | null;
	item_type: string;
	added_at: string;
}

interface ContextData {
	starred: StarredDoc[];
	sessions: SessionItem[];
	documents: ThinkingDoc[];
	library: LibraryItem[];
}

const EDGE_COLUMNS = "source_item_id, target_item_id, relation_type" as const;

async function fetchContext(): Promise<ContextData> {
	// Phase 1: parallel fetches
	const [starredRes, sessionsRes, thinkingRes, libraryRes] =
		await Promise.all([
			supabase
				.from("library_items")
				.select("id, title, doc_type, summary, updated_at, is_favorite")
				.eq("source_type", "document")
				.eq("is_favorite", true)
				.eq("is_archived", false)
				.order("updated_at", {
					ascending: false,
					nullsFirst: false,
				}),
			supabase
				.from("sessions")
				.select("id, name, summary, project_id, created_at, updated_at")
				.order("created_at", { ascending: false })
				.limit(5),
			supabase
				.from("library_items")
				.select("id, title, doc_type, summary, updated_at")
				.eq("source_type", "document")
				.eq("is_favorite", false)
				.eq("is_archived", false)
				.in("doc_type", ["idea", "plan", "research", "synthesis"])
				.order("updated_at", {
					ascending: false,
					nullsFirst: false,
				})
				.limit(15),
			supabase
				.from("library_items")
				.select(
					"id, title, item_type, added_at, content_id, contents(canonical_url)",
				)
				.neq("source_type", "document")
				.eq("is_archived", false)
				.order("added_at", { ascending: false })
				.limit(30),
		]);

	if (starredRes.error) throw starredRes.error;
	if (sessionsRes.error) throw sessionsRes.error;
	if (thinkingRes.error) throw thinkingRes.error;
	if (libraryRes.error) throw libraryRes.error;

	const starred = starredRes.data ?? [];
	const sessions = sessionsRes.data ?? [];
	const thinking = thinkingRes.data ?? [];
	const library = libraryRes.data ?? [];

	const allDocIds = [
		...starred.map((d) => d.id),
		...thinking.map((d) => d.id),
	];
	const sessionIds = sessions.map((s) => s.id);
	const thinkingDocIds = thinking.map((d) => d.id);
	const projectIds = [
		...new Set(
			sessions.map((s) => s.project_id).filter(Boolean) as string[],
		),
	];

	// Phase 2: parallel fetches
	const [edgesRes, sessionDocsRes, projectsRes, sessionMemberRes] =
		await Promise.all([
			allDocIds.length > 0
				? supabase
						.from("library_item_edges")
						.select(EDGE_COLUMNS)
						.or(
							`source_item_id.in.(${allDocIds.join(",")}),target_item_id.in.(${allDocIds.join(",")})`,
						)
				: Promise.resolve({
						data: [] as {
							source_item_id: string;
							target_item_id: string;
							relation_type: string;
						}[],
						error: null,
					}),
			sessionIds.length > 0
				? supabase
						.from("session_documents")
						.select(
							"session_id, library_item_id, library_items(id, title, doc_type, summary)",
						)
						.in("session_id", sessionIds)
				: Promise.resolve({
						data: [] as {
							session_id: string;
							library_item_id: string;
							library_items: {
								id: string;
								title: string;
								doc_type: string | null;
								summary: string | null;
							};
						}[],
						error: null,
					}),
			projectIds.length > 0
				? supabase
						.from("projects")
						.select("id, name")
						.in("id", projectIds)
				: Promise.resolve({
						data: [] as { id: string; name: string }[],
						error: null,
					}),
			thinkingDocIds.length > 0
				? supabase
						.from("session_documents")
						.select("library_item_id, sessions(name)")
						.in("library_item_id", thinkingDocIds)
				: Promise.resolve({
						data: [] as {
							library_item_id: string;
							sessions: { name: string };
						}[],
						error: null,
					}),
		]);

	if (edgesRes.error) throw edgesRes.error;
	if (sessionDocsRes.error) throw sessionDocsRes.error;
	if (projectsRes.error) throw projectsRes.error;
	if (sessionMemberRes.error) throw sessionMemberRes.error;

	const edges = (edgesRes.data ?? []) as {
		source_item_id: string;
		target_item_id: string;
		relation_type: string;
	}[];
	const sessionDocs = (sessionDocsRes.data ?? []) as {
		session_id: string;
		library_item_id: string;
		library_items: {
			id: string;
			title: string;
			doc_type: string | null;
			summary: string | null;
		};
	}[];
	const projects = (projectsRes.data ?? []) as {
		id: string;
		name: string;
	}[];
	const sessionMemberRows = (sessionMemberRes.data ?? []) as {
		library_item_id: string;
		sessions: { name: string };
	}[];

	// Phase 3: Assemble
	const allDocIdSet = new Set(allDocIds);
	const projectMap = new Map(projects.map((p) => [p.id, p.name]));
	const titleMap = new Map<string, string>();
	for (const d of starred) titleMap.set(d.id, d.title ?? "Untitled");
	for (const d of thinking) titleMap.set(d.id, d.title ?? "Untitled");

	const inboundCounts = new Map<string, number>();
	for (const e of edges) {
		if (allDocIdSet.has(e.target_item_id)) {
			inboundCounts.set(
				e.target_item_id,
				(inboundCounts.get(e.target_item_id) ?? 0) + 1,
			);
		}
	}

	const sessionMemberMap = new Map<string, string>();
	for (const row of sessionMemberRows) {
		if (!sessionMemberMap.has(row.library_item_id)) {
			sessionMemberMap.set(
				row.library_item_id,
				(row.sessions as { name: string })?.name ?? "",
			);
		}
	}

	const sessionDocMap = new Map<
		string,
		{
			documentId: string;
			title: string;
			doc_type: string | null;
			summary: string | null;
		}[]
	>();
	for (const sd of sessionDocs) {
		const li = sd.library_items;
		if (!li) continue;
		const arr = sessionDocMap.get(sd.session_id) ?? [];
		arr.push({
			documentId: li.id,
			title: li.title ?? "Untitled",
			doc_type: li.doc_type,
			summary: li.summary ?? null,
		});
		sessionDocMap.set(sd.session_id, arr);
	}

	// Resolve unknown neighbor titles
	const unknownIds = new Set<string>();
	for (const e of edges) {
		if (!titleMap.has(e.source_item_id)) unknownIds.add(e.source_item_id);
		if (!titleMap.has(e.target_item_id)) unknownIds.add(e.target_item_id);
	}
	if (unknownIds.size > 0) {
		const { data: unknownItems } = await supabase
			.from("library_items")
			.select("id, title")
			.in("id", [...unknownIds]);
		for (const item of unknownItems ?? []) {
			titleMap.set(item.id, item.title ?? "Untitled");
		}
	}

	// Exclude docs already surfaced via recent sessions
	const sessionDocIdSet = new Set<string>();
	for (const docs of sessionDocMap.values()) {
		for (const doc of docs) {
			sessionDocIdSet.add(doc.documentId);
		}
	}

	return {
		starred: starred.map((d) => ({
			documentId: d.id,
			title: d.title ?? "Untitled",
			doc_type: d.doc_type,
			summary: d.summary,
			inbound_edge_count: inboundCounts.get(d.id) ?? 0,
			updated_at: d.updated_at,
			edges: edges
				.filter((e) => e.source_item_id === d.id)
				.map((e) => ({
					relation: e.relation_type,
					neighborId: e.target_item_id,
					neighbor_title:
						titleMap.get(e.target_item_id) ?? "Untitled",
				})),
		})),
		sessions: sessions.map((s) => ({
			sessionId: s.id,
			name: s.name,
			summary: s.summary,
			project: s.project_id
				? (projectMap.get(s.project_id) ?? null)
				: null,
			created_at: s.created_at,
			updated_at: s.updated_at,
			documents: sessionDocMap.get(s.id) ?? [],
		})),
		documents: thinking
			.filter((d) => !sessionDocIdSet.has(d.id))
			.map((d) => ({
				documentId: d.id,
				title: d.title ?? "Untitled",
				doc_type: d.doc_type,
				summary: d.summary,
				inbound_edge_count: inboundCounts.get(d.id) ?? 0,
				updated_at: d.updated_at,
				session: sessionMemberMap.get(d.id) ?? null,
			})),
		library: library.map((item) => {
			const c = item.contents as { canonical_url: string } | null;
			return {
				title: item.title ?? "Untitled",
				url: c?.canonical_url ?? null,
				item_type: item.item_type,
				added_at: item.added_at,
			};
		}),
	};
}

function formatDomain(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return url;
	}
}

function buildMarkdown(data: ContextData): string {
	const lines: string[] = [];

	if (data.starred.length > 0) {
		lines.push("## Starred Documents\n");
		for (const doc of data.starred) {
			const type = doc.doc_type ? ` (${doc.doc_type})` : "";
			const summary = doc.summary ? ` -- ${doc.summary}` : "";
			const inbound =
				doc.inbound_edge_count > 0
					? ` [${doc.inbound_edge_count} inbound]`
					: "";
			lines.push(`- **${doc.title}**${type}${summary}${inbound}`);
			for (const edge of doc.edges) {
				lines.push(`  - ${edge.relation} -> ${edge.neighbor_title}`);
			}
		}
		lines.push("");
	}

	if (data.sessions.length > 0) {
		lines.push("## Recent Sessions\n");
		for (const session of data.sessions) {
			const project = session.project ? ` -- ${session.project}` : "";
			lines.push(`### ${session.name}${project}\n`);
			if (session.summary) {
				lines.push(`${session.summary}\n`);
			}
			if (session.documents.length > 0) {
				for (const doc of session.documents) {
					const type = doc.doc_type ? ` (${doc.doc_type})` : "";
					const summary = doc.summary ? ` -- ${doc.summary}` : "";
					lines.push(`- ${doc.title}${type}${summary}`);
				}
				lines.push("");
			}
		}
	}

	if (data.documents.length > 0) {
		lines.push("## Thinking Documents\n");
		for (const doc of data.documents) {
			const type = doc.doc_type ? ` (${doc.doc_type})` : "";
			const summary = doc.summary ? ` -- ${doc.summary}` : "";
			const inbound =
				doc.inbound_edge_count > 0
					? ` [${doc.inbound_edge_count} inbound]`
					: "";
			const session = doc.session ? ` -- from "${doc.session}"` : "";
			lines.push(
				`- **${doc.title}**${type}${summary}${inbound}${session}`,
			);
		}
		lines.push("");
	}

	if (data.library.length > 0) {
		lines.push("## Library\n");
		for (const item of data.library) {
			const domain =
				item.url !== null ? ` -- ${formatDomain(item.url)}` : "";
			lines.push(`- ${item.title}${domain}`);
		}
		lines.push("");
	}

	return lines.join("\n");
}

// CONTEXT: ~4 chars per token is a rough heuristic for English/JSON text
function estimateTokens(text: string): string {
	const tokens = Math.ceil(text.length / 4);
	if (tokens >= 1000) return `~${(tokens / 1000).toFixed(1)}k`;
	return `~${tokens}`;
}

function ContextPage(): ReactElement {
	const { data: session } = useSession();
	const { view } = Route.useSearch();
	const navigate = useNavigate();

	useDocumentTitle("Context");

	const isJson = view === "json";

	const contextQuery = useQuery({
		queryKey: ["context"],
		queryFn: fetchContext,
		enabled: !!session?.user?.id,
	});

	if (!session?.user) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
					<p className="text-fg-faint">
						Sign in to view your context.
					</p>
					<Link
						to="/login"
						search={{ redirect: undefined }}
						className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
					>
						Sign in
					</Link>
				</div>
			</div>
		);
	}

	if (contextQuery.isPending) {
		return <div className="mx-auto max-w-[960px] px-6 py-10" />;
	}

	if (contextQuery.isError) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
					<h1 className="text-xl text-red-400">Error</h1>
					<p className="mt-2 text-red-500">
						{contextQuery.error instanceof Error
							? contextQuery.error.message
							: "Failed to load context"}
					</p>
				</div>
			</div>
		);
	}

	const data = contextQuery.data;
	const markdown = buildMarkdown(data);
	const jsonString = JSON.stringify(data, null, 2);

	return (
		<div className="pb-24">
			<section className="mx-auto max-w-[960px] px-6 pt-8">
				<div className="mb-6 flex items-center justify-between">
					<h1 className="font-display text-fg-strong text-xl font-semibold">
						Context
					</h1>
					<div className="flex items-center gap-3">
						<span className="text-fg-faint text-xs tabular-nums">
							{estimateTokens(jsonString)} tokens
						</span>
						<button
							type="button"
							onClick={() =>
								navigate({
									to: "/context",
									search: {
										view: isJson ? undefined : "json",
									},
								})
							}
							className="text-fg-faint hover:text-fg-default border-line-subtle rounded border px-2 py-1 text-xs transition-colors"
						>
							{isJson ? "Rendered" : "JSON"}
						</button>
					</div>
				</div>
				{isJson ? (
					<pre className="bg-surface-raised border-line-subtle overflow-x-auto rounded-lg border p-4 text-xs leading-relaxed">
						<code>{jsonString}</code>
					</pre>
				) : markdown ? (
					<MarkdownRenderer markdown={markdown} />
				) : (
					<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
						<p className="text-fg-faint">
							No documents or library items yet.
						</p>
					</div>
				)}
			</section>
		</div>
	);
}
